/**
 * Calendar helpers for the period selection (fpa-backend ADR-0006): any months of one year, as of
 * a date inside them. Dates are plain `YYYY-MM-DD` strings; "today" is America/New_York, as on the
 * server. Pure: no React, no network.
 */

export const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const ALL_MONTHS: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
export const QUARTERS = [1, 2, 3, 4] as const

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const BUSINESS_TZ = 'America/New_York'

export function quarterMonths(q: number): number[] {
  return [1, 2, 3].map((i) => (q - 1) * 3 + i)
}

export function quarterOfMonth(month: number): number {
  return Math.floor((month - 1) / 3) + 1
}

/** Sorted, unique months 1-12 (anything else dropped). */
export function normalizeMonths(months: readonly number[]): number[] {
  return [...new Set(months.filter((m) => Number.isInteger(m) && m >= 1 && m <= 12))].sort((a, b) => a - b)
}

export function isContiguous(months: readonly number[]): boolean {
  return months.every((m, i) => i === 0 || m === (months[i - 1] ?? 0) + 1)
}

export function sameMonths(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((m, i) => m === b[i])
}

/** The quarter (1-4) the months are exactly, else null. */
export function wholeQuarter(months: readonly number[]): number | null {
  return QUARTERS.find((q) => sameMonths(months, quarterMonths(q))) ?? null
}

function runsOf(values: readonly number[]): [number, number][] {
  const runs: [number, number][] = []
  for (const v of values) {
    const last = runs[runs.length - 1]
    if (last && v === last[1] + 1) last[1] = v
    else runs.push([v, v])
  }
  return runs
}

/** "FY 2026", "Q3 2026", "Q1–Q2 2026", "Jan + Aug 2026" — the same rule as fpa-backend. */
export function periodLabel(year: number, monthsIn: readonly number[]): string {
  const months = normalizeMonths(monthsIn)
  if (months.length === 12) return `FY ${year}`
  const set = new Set(months)
  const whole = QUARTERS.filter((q) => quarterMonths(q).every((m) => set.has(m)))
  if (months.length > 0 && whole.length * 3 === months.length) {
    return `${runsOf(whole)
      .map(([a, b]) => (a === b ? `Q${a}` : `Q${a}–Q${b}`))
      .join(' + ')} ${year}`
  }
  const abbr = (m: number) => MONTH_ABBR[m - 1] ?? ''
  return `${runsOf(months)
    .map(([a, b]) => (a === b ? abbr(a) : `${abbr(a)}–${abbr(b)}`))
    .join(' + ')} ${year}`
}

export function isIsoDate(value: string): boolean {
  const m = ISO_DATE.exec(value)
  if (!m) return false
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3])
}

/** Today's date in America/New_York. */
export function businessToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TZ, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(now)
    .slice(0, 10)
}

const pad = (n: number) => String(n).padStart(2, '0')

export function isoOf(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

export function periodStart(year: number, months: readonly number[]): string {
  return isoOf(year, months[0] ?? 1, 1)
}

export function periodEnd(year: number, months: readonly number[]): string {
  const last = months[months.length - 1] ?? 12
  return isoOf(year, last, daysInMonth(year, last))
}

const minIso = (a: string, b: string) => (a <= b ? a : b)

/** The as-of bounds of a period: [its first day, the earlier of its last day and today]. */
export function asOfBounds(year: number, months: readonly number[], today: string): { min: string; max: string } {
  return { min: periodStart(year, months), max: minIso(periodEnd(year, months), today) }
}

/** An as-of inside the bounds, or null for "the latest" (out of range values fall back to it). */
export function clampAsOf(value: string | null, bounds: { min: string; max: string }): string | null {
  if (!value || !isIsoDate(value) || value < bounds.min) return null
  return value >= bounds.max ? null : value
}

function parts(iso: string): [number, number, number] {
  const m = ISO_DATE.exec(iso)
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : [0, 1, 1]
}

/** "Aug 26". */
export function shortDate(iso: string): string {
  const [, m, d] = parts(iso)
  return `${MONTH_ABBR[m - 1] ?? ''} ${d}`
}

/** "Aug 26, 2026". */
export function mediumDate(iso: string): string {
  const [y, m, d] = parts(iso)
  return `${MONTH_ABBR[m - 1] ?? ''} ${d}, ${y}`
}

/** "August 26, 2026". */
export function longDate(iso: string): string {
  const [y, m, d] = parts(iso)
  return `${MONTH_NAMES[m - 1] ?? ''} ${d}, ${y}`
}

/** "August 26, 2026" → "2026-08-26" (null when it has another shape). */
export function parseLongDate(value: string): string | null {
  const m = /^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/.exec(value.trim())
  if (!m) return null
  const month = MONTH_NAMES.findIndex((n) => n.toLowerCase() === (m[1] ?? '').toLowerCase()) + 1
  if (month === 0) return null
  const iso = isoOf(Number(m[3]), month, Number(m[2]))
  return isIsoDate(iso) ? iso : null
}
